import * as RadixPopover from '@radix-ui/react-popover';
import { ChevronDown, X } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from './cn';

/**
 * Поле, що відкриває панель вибору.
 *
 * Виглядає як поле вводу, а не як кнопка-чипс (`Popover`): у формі й у
 * бічній панелі фільтрів дилер чекає ряду полів однакової форми. Панель — на
 * Radix: Escape, клік назовні, повернення фокуса й портал поверх
 * `overflow: hidden` дістаються даром.
 *
 * Кнопка очищення — СУСІД тригера, а не його дитина: кнопка всередині кнопки
 * — невалідна розмітка, і читачі екрана її губили б.
 */
export function PickerField({
	id,
	value,
	placeholder,
	disabled = false,
	open,
	onOpenChange,
	onClear,
	clearLabel = 'Clear',
	panelClassName,
	children,
}: {
	/** Для `<label htmlFor>` над полем. */
	id?: string;
	/** Що показати в полі; `null` — нічого не вибрано, показується `placeholder`. */
	value: string | null;
	placeholder: string;
	disabled?: boolean;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Якщо передано і є значення — у полі зʼявляється ✕. */
	onClear?: () => void;
	clearLabel?: string;
	panelClassName?: string;
	children: ReactNode;
}): ReactNode {
	const clearable = Boolean(onClear) && value !== null && !disabled;

	return (
		/*
		  modal: панель рендериться в порталі, і всередині модального Dialog
		  (мобільна шухляда фільтрів) його пастка фокуса вважала б пошук у панелі
		  «зовнішнім» і забирала фокус назад — набрати текст було неможливо.
		  Модальна панель ставить пастку батька на паузу, як це робить Select.
		*/
		<RadixPopover.Root open={open} onOpenChange={onOpenChange} modal>
			<div data-slot="picker-field" className="relative">
				<RadixPopover.Trigger
					id={id}
					disabled={disabled}
					className={cn(
						'flex h-10 w-full items-center rounded-control border pl-3 text-left text-sm transition-colors',
						'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25',
						clearable ? 'pr-16' : 'pr-9',
						disabled
							? 'cursor-not-allowed border-transparent bg-surface-muted text-fg-subtle'
							: 'border-border-strong bg-surface text-fg hover:border-fg-muted',
						'data-[state=open]:border-primary data-[state=open]:ring-1 data-[state=open]:ring-primary',
					)}
				>
					<span className={cn('min-w-0 flex-1 truncate', value === null && !disabled && 'text-fg-muted')}>
						{value ?? placeholder}
					</span>
				</RadixPopover.Trigger>

				{clearable && (
					<button
						type="button"
						onClick={onClear}
						aria-label={clearLabel}
						className="absolute right-8 top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded-full text-fg-muted transition-colors hover:bg-surface-muted hover:text-fg"
					>
						<X className="size-3.5" strokeWidth={2.4} />
					</button>
				)}
				<ChevronDown
					aria-hidden="true"
					className={cn(
						'pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 transition-transform',
						disabled ? 'text-fg-subtle' : 'text-fg',
						open && 'rotate-180',
					)}
				/>
			</div>

			<RadixPopover.Portal>
				<RadixPopover.Content
					align="start"
					sideOffset={6}
					collisionPadding={12}
					className={cn(
						'z-50 flex w-72 min-w-(--radix-popover-trigger-width) flex-col overflow-hidden',
						'max-h-[min(30rem,var(--radix-popover-content-available-height))]',
						'rounded-card border border-border bg-surface shadow-e3',
						panelClassName,
					)}
				>
					{children}
				</RadixPopover.Content>
			</RadixPopover.Portal>
		</RadixPopover.Root>
	);
}
